import { Box, VStack, Text, Progress, useToast, IconButton, Tooltip, Collapse, Button, HStack, ScaleFade, Switch, FormControl, FormLabel } from '@chakra-ui/react'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { FaMicrophone, FaMicrophoneSlash, FaSignOutAlt, FaQrcode, FaTimes, FaBars, FaExpand, FaVolumeMute, FaVolumeUp, FaChartBar } from 'react-icons/fa'
import { subscribeToMatch, updateScore, addScoreEvent, undoLastScore, getMatch } from '@/services/firebase'
import { database } from '@/services/firebase'
import { QRCodeModal } from '@/components/modals/QRCodeModal'
import { MicrophoneSettingsModal } from '@/components/modals/MicrophoneSettingsModal'
import { Statistics } from '@/components/Statistics'
import { ScoreBox } from '@/components/scoring/ScoreBox'
import { TopBar } from '@/components/scoring/TopBar'
import { ExitModal } from '@/components/scoring/ExitModal'
import { useSpeechRecognition as useSpeechRecognitionController } from '@/hooks/useSpeechRecognitionRouter'
import { useAutoFullscreenChrome } from '@/hooks/useAutoFullscreenChrome'
import { ref, update, get, set, onValue } from 'firebase/database'
import QRCode from 'react-qr-code'
import { useScreenWakeLock } from '@/hooks/useScreenWakeLock'
import { usePaywallContext } from '@/contexts/PaywallContext'
import ClubLogo from '@/components/ClubLogo'
import { IPhoneModal } from '@/components/iPhoneModal'
import { InternetConnectionModal } from '@/components/InternetConnectionModal'

type TeamColor = 'red' | 'blue' | 'purple' | 'yellow' | 'orange' | 'green'

interface ScoringScreenProps {
  matchId: string
  team1Color: TeamColor
  team2Color: TeamColor
  isController?: boolean
  team1Name?: string
  team2Name?: string
  showQRCode?: boolean
  courtNumber?: number
}

// Define valid commands
const VALID_COMMANDS = {
  POINT: 'point',
  REVERSE_POINT: 'reverse point',
  CANCEL: 'cancel',
  RESET_MATCH: 'reset match',
  CONFIRM: 'confirm'
} as const

// Patch: Extend MatchData type to include pendingReset for linter
type MatchDataWithPendingReset = {
  pendingReset?: { timer: number } | null;
} & any;

function SayReversePointButton({ isDisabled, onClick, isController }: { isDisabled: boolean; onClick: () => void; isController: boolean }) {
  return (
    <Button
      mt={0}
      colorScheme="blue"
      variant="outline"
      size="sm"
      onClick={onClick}
      isDisabled={isDisabled || !isController}
      _hover={{ bg: 'blue.500', color: 'white' }}
      color="#747474"
      opacity={isDisabled || !isController ? 0.5 : 1}
      borderRadius="2px"
    >
      Say "Reverse Point"
    </Button>
  )
}

function SayResetMatchButton({ isDisabled, onClick, isController }: { isDisabled: boolean; onClick: () => void; isController: boolean }) {
  return (
    <Button
      mt={0}
      colorScheme="blue"
      variant="outline"
      size="sm"
      onClick={onClick}
      isDisabled={isDisabled || !isController}
      _hover={{ bg: 'blue.500', color: 'white' }}
      color="#747474"
      opacity={isDisabled || !isController ? 0.5 : 1}
      borderRadius="2px"
    >
      Say "Reset Match"
    </Button>
  )
}

export function ScoringScreen({ matchId, team1Color, team2Color, isController: propIsController = false, team1Name, team2Name, showQRCode = true, courtNumber }: ScoringScreenProps) {
  // Determine if user is a controller based on URL token parameter (deployed version logic)
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const isController = propIsController || !!searchParams.get('token')
  const { userSubscription } = usePaywallContext()
  const isIndividualPlan = userSubscription?.planType === 'individual'
  const { isActive: isWakeLockActive } = useScreenWakeLock();
  
  // Add touch event handler to keep screen active
  useEffect(() => {
    const touchHandler = () => {
      // Force screen to stay awake by adding a temporary class
      document.body.classList.add('wake-lock-active');
      setTimeout(() => {
        document.body.classList.remove('wake-lock-active');
      }, 100);
    };

    document.addEventListener('touchstart', touchHandler, { passive: true });
    document.addEventListener('touchmove', touchHandler, { passive: true });

    return () => {
      document.removeEventListener('touchstart', touchHandler);
      document.removeEventListener('touchmove', touchHandler);
    };
  }, []);

  const [showQRCodeModal, setShowQRCodeModal] = useState(false)
  const [showMicrophoneSettings, setShowMicrophoneSettings] = useState(false)
  const [showStatistics, setShowStatistics] = useState(false)
  const [showIPhoneModal, setShowIPhoneModal] = useState(false)
  const [isQRCodeVisible, setIsQRCodeVisible] = useState(showQRCode)
  const [pendingReset, setPendingReset] = useState<{ timer: number } | null>(null)
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [controllerToken, setControllerToken] = useState<string | null>(null)
  

  const [isDarkTheme, setIsDarkTheme] = useState(() => {
    const stored = localStorage.getItem('scoringThemeDark')
    return stored === 'true'
  })

  useEffect(() => {
    localStorage.setItem('scoringThemeDark', isDarkTheme.toString())
  }, [isDarkTheme])

  
  const [team1Score, setTeam1Score] = useState(0)
  const [team2Score, setTeam2Score] = useState(0)
  const [setScores, setSetScores] = useState<Array<{ team1: number; team2: number }>>([])
  const [pendingSetCompletion, setPendingSetCompletion] = useState<{
    team: 'team1' | 'team2'
    timer: number
  } | null>(null)
  const [setCompletionTimer, setSetCompletionTimer] = useState(0)
  const [lastScoringTeam, setLastScoringTeam] = useState<'team1' | 'team2' | 'none' | null>(null)
  const [cooldown, setCooldown] = useState(0)
  const toast = useToast()
  const isInCooldown = useRef(false)
  
  // Auto-fullscreen on Chrome
  const { toggleFullscreen } = useAutoFullscreenChrome()
  const keepAliveInterval = useRef<number | null>(null)
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)
  const recognitionRef = useRef<any>(null)
  const recognitionActive = useRef(false)
  const [showExitConfirmation, setShowExitConfirmation] = useState(false)
  const [isExitOpen, setIsExitOpen] = useState(false)
  const helpRef = useRef<HTMLDivElement>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [hasNotificationPermission, setHasNotificationPermission] = useState(false)
  const pendingResetRef = useRef<{ timer: number } | null>(null)
  const [showHelp, setShowHelp] = useState(false)
  const [isKicked, setIsKicked] = useState(false)
  

  const [currentControllerNumber, setCurrentControllerNumber] = useState<number | null>(null)
  const localControllerNumber = useRef<number | null>(null)

  // Add state for set completion (similar to tennis game completion)
  const setCompletionCancelledRef = useRef(false)
  const lastPointCommandTimeRef = useRef(0)
  const setCompletionActiveRef = useRef(false)
  const setCompletionInProgressRef = useRef(false)
  const lastSuccessfulCommandTimeRef = useRef(Date.now())

  // Request notification permission on component mount
  useEffect(() => {
    // Only show for iOS devices (iPad and iPhone)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    setShowIPhoneModal(isIOS)

    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        setHasNotificationPermission(true)
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          setHasNotificationPermission(permission === 'granted')
        })
      }
    }

      // Generate controller token
  const handleToken = async () => {
    try {
      // Check if there's an existing token in the URL
      const urlParams = new URLSearchParams(window.location.search)
      const existingToken = urlParams.get('token')
      const isControllerParam = urlParams.get('controller') === 'true'
      
      if (existingToken && isControllerParam) {
        // Check if this token is already being used by another user
        const controllerRef = ref(database, `matches/${matchId}/controllers/${existingToken}`)
        const controllerSnapshot = await get(controllerRef)
        
        if (controllerSnapshot.exists()) {
          const controllerData = controllerSnapshot.val()
          // Check if this token was created more than 5 seconds ago (indicating it's being shared)
          const tokenAge = Date.now() - controllerData.createdAt
          
          if (tokenAge > 5000) {
            // This is a shared token, generate a new one for this user
          } else {
            // This is a fresh token, use it
            const controllerNumber = controllerData.controllerNumber || 1
            
            // Store controller number locally for reliable checking
            localControllerNumber.current = controllerNumber
            setCurrentControllerNumber(controllerNumber)
            setControllerToken(existingToken)
            return
          }
        }
      }
      
      // Check if user is already the host controller before generating new token
      const controllersRef = ref(database, `matches/${matchId}/controllers`)
      const controllersSnapshot = await get(controllersRef)
      
      if (controllersSnapshot.exists()) {
        const controllers = controllersSnapshot.val()
        const controllerEntries = Object.entries(controllers)
        
        // Find the host controller (controller #1)
        const hostControllerEntry = controllerEntries.find(([token, data]: [string, any]) => {
          return data.controllerNumber === 1
        })
        
        if (hostControllerEntry) {
          const [hostToken, hostData] = hostControllerEntry as [string, any]
          
          // Check if this user should use the host token
          // If there's an existing token in URL and it matches the host token, use it
          if (existingToken === hostToken) {
            // Store controller number locally for reliable checking
            localControllerNumber.current = hostData.controllerNumber
            setCurrentControllerNumber(hostData.controllerNumber)
            setControllerToken(hostToken)
            return
          }
          
          // If no existing token in URL and this is the first controller, use the host token
          if (!existingToken && controllerEntries.length === 1) {
            // Store controller number locally for reliable checking
            localControllerNumber.current = hostData.controllerNumber
            setCurrentControllerNumber(hostData.controllerNumber)
            setControllerToken(hostToken)
            return
          }
        }
      }
      
      // Generate new token if no existing token, token doesn't exist, or it's a shared token
      const newToken = crypto.randomUUID()
      const controllerRef = ref(database, `matches/${matchId}/controllers/${newToken}`)
      
      // Get existing controllers to assign controller number
      let controllerNumber = 1
      
      if (controllersSnapshot.exists()) {
        const controllers = controllersSnapshot.val()
        const existingNumbers = Object.values(controllers).map((c: any) => c.controllerNumber || 0)
        controllerNumber = Math.max(...existingNumbers, 0) + 1
      }
      
      await set(controllerRef, {
        createdAt: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
        isActive: true,
        permissions: ['score', 'control'],
        controllerNumber: controllerNumber
      })

      // Store controller number locally for reliable checking
      localControllerNumber.current = controllerNumber
      setCurrentControllerNumber(controllerNumber)
      setControllerToken(newToken)
      
    } catch (error) {
      console.error('❌ Token generation error:', error)
    }
  }

    if (isController) {
      handleToken()
    }
  }, [matchId, isController])

  const toggleMute = async () => {
    if (!hasNotificationPermission && 'Notification' in window) {
      const permission = await Notification.requestPermission()
      setHasNotificationPermission(permission === 'granted')
    }
    setIsMuted(!isMuted)
    
    // Show guidance toast
    toast({
      title: 'Please Mute Notifications',
      description: 'To mute chimes, go to your device\'s sound settings and turn down/off "Notification" volume. This won\'t affect your Ringtone or Message (Media) notifications.',
      status: 'info',
      duration: 12000,
      isClosable: true,
      position: 'top'
    })
  }


  const showToast = (title: string, description: string, status: 'info' | 'warning' | 'success' | 'error' = 'info') => {
    if (!isMuted) {
      toast({
        title,
        description,
        status,
        duration: 3000,
        isClosable: true,
      })

      // Show system notification if permission is granted
      if (hasNotificationPermission && 'Notification' in window) {
        new Notification(title, {
          body: description,
          icon: '/favicon.ico'
        })
      }
    }
  }

  useEffect(() => {
    const unsubscribe = subscribeToMatch(matchId, (data: MatchDataWithPendingReset) => {
      // Check if match was deleted
      if (!data) {
        showToast(
          'Match Deleted',
          `The match has been deleted - This court #${courtNumber} has been used to start a new match on another screen.`,
          'warning'
        )
        // Redirect to landing page after a short delay
        setTimeout(() => {
          window.location.href = '/'
        }, 15000)
        return
      }
      
      if (data.scores) {
        setTeam1Score(data.scores.team1 || 0)
        setTeam2Score(data.scores.team2 || 0)
      }
      // Always update set scores from Firebase (including empty array on reset)
      setSetScores(data.setScores || [])
      // Update cooldown state if present
      if (data.scores && data.scores.cooldown !== undefined) {
        setCooldown(data.scores.cooldown)
        if (data.scores.lastScoringTeam !== undefined) {
          setLastScoringTeam(data.scores.lastScoringTeam as 'team1' | 'team2' | 'none' | null)
        }
      }
      // Update pending reset state if present
      if (data.pendingReset) {
        // If Firebase has pendingReset but we don't, start the timer
        if (!pendingResetRef.current) {
          pendingResetRef.current = data.pendingReset
          setPendingReset(data.pendingReset)
          
          // Start the timer countdown
          if (resetTimerRef.current) {
            clearInterval(resetTimerRef.current)
          }
          
          resetTimerRef.current = setInterval(() => {
            setPendingReset(prev => {
              if (!prev) {
                clearInterval(resetTimerRef.current!)
                resetTimerRef.current = null
                return null
              }
              const newTimer = prev.timer - 1
              if (newTimer <= 0) {
                if (resetTimerRef.current) {
                  clearInterval(resetTimerRef.current)
                  resetTimerRef.current = null
                }
                pendingResetRef.current = null
                // Clear pendingReset in Firebase when timer expires
                const matchRef = ref(database, `matches/${matchId}`)
                update(matchRef, { pendingReset: null }).catch(error => {
                  console.error('Error clearing Firebase pendingReset on timer expiration (from sync):', error)
                })
                return null
              }
              pendingResetRef.current = { timer: newTimer }
              // Update timer in Firebase
              const matchRef = ref(database, `matches/${matchId}`)
              update(matchRef, { pendingReset: { timer: newTimer } }).catch(error => {
                console.error('Error updating Firebase timer (from sync):', error)
              })
              return { timer: newTimer }
            })
          }, 1000)
        } else if (pendingResetRef.current.timer !== data.pendingReset.timer) {
          // Timer value changed, update our state to match Firebase
          setPendingReset(data.pendingReset)
          pendingResetRef.current = data.pendingReset
        }
      } else {
        // Firebase has no pendingReset, clear our state
        if (resetTimerRef.current) {
          clearInterval(resetTimerRef.current)
          resetTimerRef.current = null
        }
        pendingResetRef.current = null
        setPendingReset(null)
      }
      // Update pending set completion state if present
      if (data.pendingSetCompletion && typeof data.pendingSetCompletion === 'object' && 'team' in data.pendingSetCompletion && 'timer' in data.pendingSetCompletion) {
        setPendingSetCompletion(data.pendingSetCompletion)
        setSetCompletionTimer((data.pendingSetCompletion as { timer: number }).timer)
      } else {
        setPendingSetCompletion(null)
        setSetCompletionTimer(0)
      }
      // Update showQRCode from server
      if (data.showQRCode !== undefined) {
        setIsQRCodeVisible(data.showQRCode)
      }
    })

    return () => {
      unsubscribe()
    }
  }, [matchId])

  // Check for set win condition
  useEffect(() => {
    // Skip this logic if there's a pending set completion (new system is handling it)
    if (pendingSetCompletion || setCompletionActiveRef.current) {
      return
    }
  }, [pendingSetCompletion, setCompletionActiveRef.current])

  // Cooldown timer
  useEffect(() => {
    if (cooldown > 0) {
      isInCooldown.current = true
      // Only pause speech recognition during cooldown if there's no pending set completion
      if (recognitionRef.current && !pendingSetCompletion) {
        try {
          recognitionRef.current.stop()
          recognitionActive.current = false
        } catch (error) {
          console.error('Error stopping recognition during cooldown:', error)
        }
      }

      const timer = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            isInCooldown.current = false
            // Resume speech recognition after cooldown if there's no pending set completion
            if (recognitionRef.current && !recognitionActive.current && !pendingSetCompletion) {
              try {
                // Add a small delay before restarting
                setTimeout(() => {
                  if (!recognitionActive.current && !isInCooldown.current && !pendingSetCompletion) {
                    recognitionRef.current?.start()
                  }
                }, 100)
              } catch (error) {
                console.error('Error restarting recognition after cooldown:', error)
              }
            }
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    } else {
      // Reset cooldown ref when cooldown is 0
      isInCooldown.current = false
    }
  }, [cooldown, pendingSetCompletion])

  // Add new useEffect for set completion timer
  useEffect(() => {
    if (pendingSetCompletion) {
      const { team } = pendingSetCompletion;
      setLastScoringTeam(team); // Update lastScoringTeam when set completion is triggered
      const timer = setInterval(() => {
        setSetCompletionTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Auto-complete the set if no cancel - only if this is a controller
            if (isController) {
              const { team } = pendingSetCompletion;
              const newTeam1Score = team === 'team1' ? team1Score + 1 : team1Score;
              const newTeam2Score = team === 'team2' ? team2Score + 1 : team2Score;
              
              handleSetCompletionWithPoint(team, newTeam1Score, newTeam2Score);
            } else {
              // Display-only devices just clear their local state
              setPendingSetCompletion(null);
              setCompletionActiveRef.current = false;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [pendingSetCompletion, team1Score, team2Score, matchId, isController]);

  // Keep-alive function to maintain connection
  const startKeepAlive = () => {
    if (keepAliveInterval.current) return

    keepAliveInterval.current = window.setInterval(() => {
      if (recognitionRef.current && !recognitionActive.current && !isInCooldown.current) {
        try {
          recognitionRef.current.start()
        } catch (error) {
          console.error('Keep-alive restart failed:', error)
        }
      }
    }, 1000) // Check every second
  }

  // Stop keep-alive
  const stopKeepAlive = () => {
    if (keepAliveInterval.current) {
      clearInterval(keepAliveInterval.current)
      keepAliveInterval.current = null
    }
  }

  // Request wake lock to prevent device sleep
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await (navigator as any).wakeLock.request('screen')
      }
    } catch (error) {
      console.error('Wake Lock request failed:', error)
    }
  }

  // Release wake lock
  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release()
        wakeLockRef.current = null
      } catch (error) {
        console.error('Wake Lock release failed:', error)
      }
    }
  }

  const handleCommand = async (command: string[]) => {
    if (!isController || isKicked) {
      return
    }
    
    // FAILSAFE: Clear stuck blocking refs if no successful command in 8.2+ seconds
    const timeSinceLastSuccess = Date.now() - lastSuccessfulCommandTimeRef.current
    if (timeSinceLastSuccess > 8200) {
      console.log('🔧 FAILSAFE: Clearing stuck blocking refs (no successful commands for 8.2+ seconds)')
      setCompletionActiveRef.current = false
      setCompletionInProgressRef.current = false
      isInCooldown.current = false
      // Don't clear Firebase state - only local refs
    }
    
    // Check if this is a cancel command
    const isCancelCommand = command.some((alt: string) => 
      alt.toLowerCase().includes('cancel') || 
      alt.toLowerCase().includes('cancel set')
    )

    // Check for reset match command
    const isResetMatch = command.some((alt: string) => {
      const lowerAlt = alt.toLowerCase().trim()
      return lowerAlt === 'reset match' ||
             lowerAlt === 'resetmatch' ||
             lowerAlt === 'reset mach' ||
             lowerAlt === 'resetmach' ||
             lowerAlt === 'reset mac' ||
             lowerAlt === 'resetmac' ||
             lowerAlt === 'reset much' ||
             lowerAlt === 'resetmuch' ||
             lowerAlt === 'reset mush' ||
             lowerAlt === 'resetmush' ||
             lowerAlt === 'reset mash' ||
             lowerAlt === 'resetmash' ||
             lowerAlt === 'reset math' ||
             lowerAlt === 'resetmath' ||
             lowerAlt === 'reset max' ||
             lowerAlt === 'resetmax' ||
             lowerAlt === 'recent match' ||
             lowerAlt === 'recentmatch'
    })

    // Handle reset match command first
    if (isResetMatch) {
      if (!isController) {
        return
      }
      handleResetMatch()
      return
    }

    // Check for confirm command when there's a pending reset
    const isConfirmCommand = command.some((alt: string) => {
      const trimmed = alt.toLowerCase().trim()
      return trimmed === 'confirm' || trimmed === 'confirmed' || trimmed === 'confirms' || trimmed === 'confirming'
    })

    // Block all commands except "Cancel" and "Confirm" during cooldown
    if (!isCancelCommand && !isConfirmCommand && isInCooldown.current) {
      return
    }

    // Block all commands except "Cancel" and "Confirm" during pending reset
    if ((pendingReset || pendingResetRef.current) && !isCancelCommand && !isConfirmCommand) {
      return
    }

    // If there's a pending set completion, only allow cancel command
    if ((pendingSetCompletion || setCompletionActiveRef.current) && !isCancelCommand) {
      return
    }

    // Handle cancel command for set completion
    if (isCancelCommand && pendingSetCompletion) {
      // Clear pending set completion in Firebase
      const matchRef = ref(database, `matches/${matchId}`)
      update(matchRef, {
        pendingSetCompletion: null
      }).catch(error => {
        console.error('Error clearing pending set completion:', error)
      })
      // Clear the active refs when canceling
      setCompletionActiveRef.current = false
      setCompletionInProgressRef.current = false
      lastSuccessfulCommandTimeRef.current = Date.now()
      return
    }

    // Handle cancel command for reset
    if (isCancelCommand && (pendingReset || pendingResetRef.current)) {
      if (resetTimerRef.current) {
        clearInterval(resetTimerRef.current)
        resetTimerRef.current = null
      }
      pendingResetRef.current = null
      setPendingReset(null)
      return
    }

    // Check for confirm command when there's a pending reset
    if (isConfirmCommand && (pendingReset || pendingResetRef.current)) {
      handleConfirmReset()
      return
    }

    // Check for reverse point command
    const isReversePoint = command.some((alt: string) => {
      const lowerAlt = alt.toLowerCase().trim()
      return lowerAlt === 'reverse point' ||
             lowerAlt === 'reverse a point' ||
             lowerAlt === 'reverse one point' ||
             lowerAlt === 'reversepoint' ||
             lowerAlt === 'verse point' ||
             lowerAlt === 'versepoint' ||
             lowerAlt === 'vs point' ||
             lowerAlt.includes('vs point')
    })

    // Handle reverse point command
    if (isReversePoint) {
      try {
        // Fetch the latest match data from Firebase first
        const matchData = await getMatch(matchId)
        if (!matchData) {
          console.error('Failed to fetch latest match data')
          return
        }

        // Check both local and server state for pending set completion
        if (pendingSetCompletion || matchData.pendingSetCompletion) {
          return
        }

        if (!matchData.scores) {
          console.error('Failed to fetch latest scores')
          return
        }

        const latestTeam1Score = matchData.scores.team1 || 0
        const latestTeam2Score = matchData.scores.team2 || 0

        // Check if both scores are 0
        if (latestTeam1Score === 0 && latestTeam2Score === 0) {
          return
        }

        await undoLastScore(matchId)
        lastSuccessfulCommandTimeRef.current = Date.now()
      } catch (error: any) {
        console.error('Error reversing score:', error)
        showToast('Error', 'Please wait 2 seconds between reverse operations', 'error')
      }
      return
    }

    // Check for team points - simplified since speech recognition hook handles all variations
    const isTeam1Point = command.some((alt: string) => {
      const lowerAlt = alt.toLowerCase().trim()
      
      // Check if command contains the team color + "point"
      const exactColorMatch = lowerAlt.includes(`${team1Color} point`) || lowerAlt.includes(`${team1Color}point`)
      
      // For red team, also check for variations
      if (team1Color === 'red') {
        return exactColorMatch || 
               lowerAlt.includes('read point') || 
               lowerAlt.includes('readpoint') ||
               lowerAlt.includes('reads point') ||
               lowerAlt.includes('tread point') ||
               lowerAlt.includes('treadpoint') ||
               lowerAlt.includes('treads point') ||
               lowerAlt.includes('trade point') ||
               lowerAlt.includes('tradepoint') ||
               lowerAlt.includes('trades point') ||
               lowerAlt.includes('rex point') ||
               lowerAlt.includes('rexpoint') ||
               lowerAlt.includes('head point') ||
               lowerAlt.includes('heads point') ||
               lowerAlt.includes('romeo point') ||
               lowerAlt.includes('romeopoint') ||
               lowerAlt.includes('romeos point') ||
               lowerAlt.includes('romeo\'s point')
      }
      
      // For blue team, also check for variations
      if (team1Color === 'blue') {
        return exactColorMatch || 
               lowerAlt.includes('bluepoint') ||
               lowerAlt.includes('view point') ||
               lowerAlt.includes('views point') ||
               lowerAlt.includes('viewpoint') ||
               lowerAlt.includes('flew point') || 
               lowerAlt.includes('flews point') ||
               lowerAlt.includes('flu point') ||
               lowerAlt.includes('flus point') ||
               lowerAlt.includes('through point') ||
               lowerAlt.includes('throughs point') ||
               lowerAlt.includes('true point') ||
               lowerAlt.includes('true\'s point') ||
               lowerAlt.includes('lose point') ||
               lowerAlt.includes('bravo point') ||
               lowerAlt.includes('bravos point') ||
               lowerAlt.includes('brother point') ||
               lowerAlt.includes('brothers point') ||
               lowerAlt.includes('you point') ||
               lowerAlt.includes('youpoint') ||
               lowerAlt.includes('three point') ||
               lowerAlt.includes('3.')
      }
      
      // For green team, also check for variations
      if (team1Color === 'green') {
        return exactColorMatch || 
               lowerAlt.includes('greenpoint') ||
               lowerAlt.includes('cream point') || 
               lowerAlt.includes('creampoint') ||
               lowerAlt.includes('creams point') ||
               lowerAlt.includes('seen point') ||
               lowerAlt.includes('seenpoint') ||
               lowerAlt.includes('seens point') ||
               lowerAlt.includes('seem point') ||
               lowerAlt.includes('seempoint') ||
               lowerAlt.includes('seems point') ||
               lowerAlt.includes('golf point') ||
               lowerAlt.includes('golfpoint') ||
               lowerAlt.includes('golfs point') ||
               lowerAlt.includes('screen point') ||
               lowerAlt.includes('screenpoint')
      }
      
      // For yellow team, also check for variations
      if (team1Color === 'yellow') {
        return exactColorMatch || 
               lowerAlt.includes('hello point') || 
               lowerAlt.includes('hellopoint') ||
               lowerAlt.includes('hello is point') ||
               lowerAlt.includes('hello\'s point') ||
               lowerAlt.includes('hellos point') ||
               lowerAlt.includes('yankee point') ||
               lowerAlt.includes('yankee\'s point') ||
               lowerAlt.includes('yankees point')
      }
      
      // For orange team, also check for variations
      if (team1Color === 'orange') {
        return exactColorMatch || 
               lowerAlt.includes('oscar point') || 
               lowerAlt.includes('oscarpoint') ||
               lowerAlt.includes('oscars point') ||
               lowerAlt.includes('range point') ||
               lowerAlt.includes('rangepoint') ||
               lowerAlt.includes('ranges point')
      }
      
      // For purple team, also check for variations
      if (team1Color === 'purple') {
        return exactColorMatch || 
               lowerAlt.includes('charlie point') || 
               lowerAlt.includes('charliepoint') ||
               lowerAlt.includes('charlies point') ||
               lowerAlt.includes('charlie\'s point') ||
               lowerAlt.includes('triple point')
      }
      
      return exactColorMatch
    })

    const isTeam2Point = command.some((alt: string) => {
      const lowerAlt = alt.toLowerCase().trim()
      
      // Check if command contains the team color + "point"
      const exactColorMatch = lowerAlt.includes(`${team2Color} point`) || lowerAlt.includes(`${team2Color}point`)
      
      // For red team, also check for variations
      if (team2Color === 'red') {
        return exactColorMatch || 
               lowerAlt.includes('read point') || 
               lowerAlt.includes('readpoint') ||
               lowerAlt.includes('reads point') ||
               lowerAlt.includes('tread point') ||
               lowerAlt.includes('treadpoint') ||
               lowerAlt.includes('treads point') ||
               lowerAlt.includes('trade point') ||
               lowerAlt.includes('tradepoint') ||
               lowerAlt.includes('trades point') ||
               lowerAlt.includes('rex point') ||
               lowerAlt.includes('rexpoint') ||
               lowerAlt.includes('head point') ||
               lowerAlt.includes('heads point') ||
               lowerAlt.includes('romeo point') ||
               lowerAlt.includes('romeopoint') ||
               lowerAlt.includes('romeos point') ||
               lowerAlt.includes('romeo\'s point')
      }
      
      // For blue team, also check for variations
      if (team2Color === 'blue') {
        return exactColorMatch || 
               lowerAlt.includes('bluepoint') ||
               lowerAlt.includes('view point') ||
               lowerAlt.includes('views point') ||
               lowerAlt.includes('viewpoint') ||
               lowerAlt.includes('flew point') || 
               lowerAlt.includes('flews point') ||
               lowerAlt.includes('flu point') ||
               lowerAlt.includes('flus point') ||
               lowerAlt.includes('through point') ||
               lowerAlt.includes('throughs point') ||
               lowerAlt.includes('true point') ||
               lowerAlt.includes('true\'s point') ||
               lowerAlt.includes('lose point') ||
               lowerAlt.includes('bravo point') ||
               lowerAlt.includes('bravos point') ||
               lowerAlt.includes('brother point') ||
               lowerAlt.includes('brothers point') ||
               lowerAlt.includes('three point') ||
               lowerAlt.includes('3 point')
      }
      
      // For green team, also check for variations
      if (team2Color === 'green') {
        return exactColorMatch || 
               lowerAlt.includes('greenpoint') ||
               lowerAlt.includes('cream point') || 
               lowerAlt.includes('creampoint') ||
               lowerAlt.includes('creams point') ||
               lowerAlt.includes('seen point') ||
               lowerAlt.includes('seenpoint') ||
               lowerAlt.includes('seens point') ||
               lowerAlt.includes('seem point') ||
               lowerAlt.includes('seempoint') ||
               lowerAlt.includes('seems point') ||
               lowerAlt.includes('golf point') ||
               lowerAlt.includes('golfpoint') ||
               lowerAlt.includes('golfs point') ||
               lowerAlt.includes('screen point') ||
               lowerAlt.includes('screenpoint')
      }
      
      // For yellow team, also check for variations
      if (team2Color === 'yellow') {
        return exactColorMatch || 
               lowerAlt.includes('hello point') || 
               lowerAlt.includes('hellopoint') ||
               lowerAlt.includes('hello is point') ||
               lowerAlt.includes('hello\'s point') ||
               lowerAlt.includes('hellos point') ||
               lowerAlt.includes('yankee point') ||
               lowerAlt.includes('yankee\'s point') ||
               lowerAlt.includes('yankees point') ||
               lowerAlt.includes('yellow') ||
               lowerAlt.includes('hello')
      }
      
      // For orange team, also check for variations
      if (team2Color === 'orange') {
        return exactColorMatch || 
               lowerAlt.includes('oscar point') || 
               lowerAlt.includes('oscarpoint') ||
               lowerAlt.includes('oscars point') ||
               lowerAlt.includes('range point') ||
               lowerAlt.includes('rangepoint') ||
               lowerAlt.includes('ranges point') ||
               lowerAlt.includes('all point') ||
               lowerAlt.includes('on point') ||
               lowerAlt.includes('armed point') ||
               lowerAlt.includes('orange')
      }
      
      // For purple team, also check for variations
      if (team2Color === 'purple') {
        return exactColorMatch || 
               lowerAlt.includes('charlie point') || 
               lowerAlt.includes('charliepoint') ||
               lowerAlt.includes('charlies point') ||
               lowerAlt.includes('charlie\'s point')
      }
      
      return exactColorMatch
    })

    // Handle team point commands
    if (isTeam1Point || isTeam2Point) {
      const scoringTeam = isTeam1Point ? 'team1' : 'team2'
      lastPointCommandTimeRef.current = Date.now()
      lastSuccessfulCommandTimeRef.current = Date.now() // Update immediately to prevent failsafe interference
      
      // IMMEDIATE UI UPDATE - Update local state first for instant feedback
      if (scoringTeam === 'team1') {
        setTeam1Score(prev => prev + 1)
        setLastScoringTeam('team1')
      } else {
        setTeam2Score(prev => prev + 1)
        setLastScoringTeam('team2')
      }
      
      // Note: Cooldown is handled by the speech recognition hook
      
      // Now handle Firebase operations in the background
      (async () => {
        try {
          // Fetch the latest match data from Firebase first
          const matchData = await getMatch(matchId)
          if (!matchData) {
            console.error('Failed to fetch latest match data')
            return
          }

          // Check both local and server state for pending set completion
          if (pendingSetCompletion || matchData.pendingSetCompletion) {
            return
          }

          if (!matchData.scores) {
            console.error('Failed to fetch latest scores')
            return
          }

          const latestTeam1Score = matchData.scores.team1 || 0
          const latestTeam2Score = matchData.scores.team2 || 0

          // Calculate new scores
          const newTeam1Score = scoringTeam === 'team1' ? latestTeam1Score + 1 : latestTeam1Score
          const newTeam2Score = scoringTeam === 'team2' ? latestTeam2Score + 1 : latestTeam2Score

          // Check if this point would complete the set BEFORE adding it
          const wouldCompleteSet = await checkSetCompletion(newTeam1Score, newTeam2Score)
          
          if (wouldCompleteSet) {
            // Check if a set completion was recently cancelled
            if (setCompletionCancelledRef.current) {
              return
            }

            // Check if set completion countdown is already active to prevent duplicate triggers
            if (setCompletionActiveRef.current) {
              return
            }

            // Set the ref immediately to block commands during countdown
            setCompletionActiveRef.current = true
            
            setPendingSetCompletion({
              team: scoringTeam,
              timer: 8
            })
            setCompletionCancelledRef.current = false
            
            // Update Firebase with pending set completion
            const matchRef = ref(database, `matches/${matchId}`)
            await update(matchRef, {
              pendingSetCompletion: {
                team: scoringTeam,
                timer: 8
              }
            })
            
            lastSuccessfulCommandTimeRef.current = Date.now()
            
            // Don't add the point yet - wait for timer or cancel
            return
          }

          // If set would not be complete, add the point normally
          await updateScore(matchId, newTeam1Score, newTeam2Score, scoringTeam)
          
          // Add score event to Firebase
          await addScoreEvent(matchId, scoringTeam, 1)
          
          lastSuccessfulCommandTimeRef.current = Date.now()
          
        } catch (error: any) {
          console.error('Error updating score:', error)
          // showToast('Error', 'Failed to update score. Please try again.', 'error')
        }
      })()
      return
    }

    // Convert command array to lowercase string for comparison
    const commandStr = command.join(' ').toLowerCase()

    if (commandStr === VALID_COMMANDS.RESET_MATCH) {
      handleResetMatch()
      return
    }

    if (commandStr === VALID_COMMANDS.CONFIRM && pendingReset) {
      handleConfirmReset()
      return
    }
  }

  // Create a ref to hold the latest handleCommand function
  const handleCommandRef = useRef(handleCommand);
  useEffect(() => {
    handleCommandRef.current = handleCommand;
  }, [handleCommand]);

  // Use the speech recognition hook with stable function reference
  const { transcript, error: lastError, hasNetworkError } = useSpeechRecognitionController({
    handleCommand: useCallback((args: string[]) => {
      handleCommandRef.current(args)
    }, []),
    isController,
    team1Color,
    team2Color,
  })

  // Add cleanup for reset timer
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearInterval(resetTimerRef.current)
      }
    }
  }, [])

  // Handle reset timer
  useEffect(() => {
    if (pendingReset) {
      if (resetTimerRef.current) {
        clearInterval(resetTimerRef.current)
      }
      
      resetTimerRef.current = setInterval(() => {
        setPendingReset(prev => {
          if (!prev) return null
          const newTimer = prev.timer - 1
          if (newTimer <= 0) {
            if (resetTimerRef.current) {
              clearInterval(resetTimerRef.current)
              resetTimerRef.current = null
            }
            pendingResetRef.current = null
            // Clear pendingReset in Firebase when timer expires
            const matchRef = ref(database, `matches/${matchId}`)
            update(matchRef, { pendingReset: null }).catch(error => {
              console.error('Error clearing Firebase pendingReset on timer expiration (from handleResetMatch):', error)
            })
            return null
          }
          pendingResetRef.current = { timer: newTimer }
          // Don't update Firebase here - let the Firebase sync timer handle it
          return { timer: newTimer }
        })
      }, 1000)
    }
  }, [pendingReset])

  // Update ref when state changes
  useEffect(() => {
    pendingResetRef.current = pendingReset
  }, [pendingReset])

  const handleResetMatch = () => {
    // Clear any existing timer first
    if (resetTimerRef.current) {
      clearInterval(resetTimerRef.current)
      resetTimerRef.current = null
    }

    // Set the initial state
    const initialTimer = 12
    pendingResetRef.current = { timer: initialTimer }
    setPendingReset({ timer: initialTimer })

    // Update Firebase so all devices see the pending reset
    const matchRef = ref(database, `matches/${matchId}`)
    update(matchRef, {
      pendingReset: { timer: initialTimer }
    }).catch(error => {
      console.error('Error updating Firebase with pendingReset:', error)
    })

    // Start the timer countdown
    resetTimerRef.current = setInterval(() => {
      setPendingReset(prev => {
        if (!prev) return null
        const newTimer = prev.timer - 1
        if (newTimer <= 0) {
          if (resetTimerRef.current) {
            clearInterval(resetTimerRef.current)
            resetTimerRef.current = null
          }
          pendingResetRef.current = null
          // Clear pendingReset in Firebase when timer expires
          const matchRef = ref(database, `matches/${matchId}`)
          update(matchRef, { pendingReset: null }).then(() => {
          }).catch(error => {
            console.error('❌ Error clearing Firebase pendingReset on timer expiration (from handleResetMatch):', error)
          })
          return null
        }
        pendingResetRef.current = { timer: newTimer }
        // Don't update Firebase here - let the Firebase sync timer handle it
        return { timer: newTimer }
      })
    }, 1000)
  }

  const handleConfirmReset = async () => {
    if (!pendingResetRef.current) {
      return
    }

    try {
      // Clear the timer first
      if (resetTimerRef.current) {
        clearInterval(resetTimerRef.current)
        resetTimerRef.current = null
      }

              // Clean up old controllers before resetting (10 minutes, except host)
        await cleanupOldControllers(matchId, 10) // 10 minutes

      // First update the set scores to ensure all devices get the update
      const matchRef = ref(database, `matches/${matchId}`)
      await update(matchRef, {
        setScores: [],  // Clear set scores first
        scores: {
          team1: 0,
          team2: 0,
          updatedAt: Date.now()
        },
        pendingSetCompletion: null,
        events: {},  // Clear all events
        pendingReset: null, // Clear pendingReset in Firebase
      })
      
      // Reset local state after Firebase update
      setTeam1Score(0)
      setTeam2Score(0)
      setSetScores([]) // Explicitly clear set scores
      setCooldown(0)
      setLastScoringTeam(null)
      pendingResetRef.current = null
      setPendingReset(null)
      lastSuccessfulCommandTimeRef.current = Date.now()
    } catch (error) {
      console.error('Error resetting match:', error)
      showToast('Error', 'Failed to reset match. Please try again.', 'error')
    }
  }

  const handleExit = () => {
    setIsExitOpen(true)
  }

  const handleConfirmExit = () => {
    // For the main device, navigate back to main page
    window.location.href = '/'
  }

  useEffect(() => {
    const validateControllerToken = async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const token = urlParams.get('token')
      const matchId = urlParams.get('matchId')
      const isControllerParam = urlParams.get('controller') === 'true'

      if (!token || !matchId || !isControllerParam) {
        return
      }
      
      try {
        const controllerRef = ref(database, `matches/${matchId}/controllers/${token}`)
        const controllerSnapshot = await get(controllerRef)

        if (!controllerSnapshot.exists()) {
          // showToast('Invalid controller access', 'This controller token is not valid', 'error')
          return
        }

        const controllerData = controllerSnapshot.val()
        const tokenAge = Date.now() - controllerData.createdAt
        
        // If token is older than 5 seconds or was accessed via QR code, generate a new one
        if (tokenAge > 5000 || window.performance.navigation.type === 0) {
          // Generate new token
          const newToken = crypto.randomUUID()
          const newControllerRef = ref(database, `matches/${matchId}/controllers/${newToken}`)
          
          // Get next available controller number
          const controllersRef = ref(database, `matches/${matchId}/controllers`)
          const controllersSnapshot = await get(controllersRef)
          let nextControllerNumber = 1
          
          if (controllersSnapshot.exists()) {
            const controllers = controllersSnapshot.val()
            const usedNumbers = Object.values(controllers).map((c: any) => c.controllerNumber)
            while (usedNumbers.includes(nextControllerNumber)) {
              nextControllerNumber++
            }
          }
          
          // Create new controller entry
          await set(newControllerRef, {
            createdAt: Date.now(),
            expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
            isActive: true,
            permissions: ['score', 'control'],
            controllerNumber: nextControllerNumber
          })
          
          // Update URL with new token without causing refresh
          const newUrl = new URL(window.location.href)
          newUrl.searchParams.set('token', newToken)
          window.history.replaceState({}, '', newUrl.toString())
          
          // Update local state
          setControllerToken(newToken)
        } else {
          // This is a fresh token, use it
          setControllerToken(token)
        }
      } catch (error) {
        console.error('Error validating controller token:', error)
        // showToast('Error', 'Error validating controller access. Please try again', 'error')
      }
    }

    validateControllerToken()
  }, [toast])

  // Add kicked controller detection
  useEffect(() => {
    if (!controllerToken || !matchId) {
      return
    }

    const controllerRef = ref(database, `matches/${matchId}/controllers/${controllerToken}`)
    
    // Initial check
    get(controllerRef).then(snapshot => {
      if (snapshot.exists()) {
        const controllerData = snapshot.val()
        if (controllerData.controllerNumber) {
          // Set controller number if available
          setCurrentControllerNumber(controllerData.controllerNumber)
        }
      }
    })

    // Listen for controller entry changes (kicked detection)
    // This should work for any user with a token, not just controllers
    const unsubscribe = onValue(controllerRef, (snapshot) => {
      if (!snapshot.exists()) {
        // Check if this is the host controller (controller #1)
        const checkIfHostController = async () => {
          try {
            const controllersRef = ref(database, `matches/${matchId}/controllers`)
            const controllersSnapshot = await get(controllersRef)
            
            if (controllersSnapshot.exists()) {
              const controllers = controllersSnapshot.val()
              const controllerEntries = Object.entries(controllers)
              
              // Find the host controller (controller #1)
              const hostControllerEntry = controllerEntries.find(([token, data]: [string, any]) => {
                return data.controllerNumber === 1
              })
              
              const hostControllerToken = hostControllerEntry?.[0]
              const isHostController = hostControllerToken === controllerToken
              
              // NEVER show kicked modal for host controller
              if (!isHostController) {
                setIsKicked(true)
              } else {
                // Ensure host is never marked as kicked
                setIsKicked(false)
              }
            }
          } catch (error) {
            console.error('❌ Error checking host controller:', error)
            // Fallback to old logic - but still protect host
            if (localControllerNumber.current !== 1) {
              setIsKicked(true)
            } else {
              // Ensure host is never marked as kicked
              setIsKicked(false)
            }
          }
        }
        
        checkIfHostController()
      }
    })

    return () => unsubscribe()
  }, [controllerToken, matchId, currentControllerNumber])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(event.target as Node)) {
        setShowHelp(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Helper function to get team color
  const getTeamColor = (color: TeamColor) => {
    return color === 'blue' ? '#2e4fcb' : 
           color === 'red' ? '#dd0000' : 
           color === 'green' ? '#38a169' : 
           color === 'orange' ? '#f73e01' : 
           color === 'purple' ? '#8f00c7' : 
           color === 'yellow' ? '#e2e700' : `${color}.100`
  }

  // Add set completion detection logic
  const checkSetCompletion = async (team1Points: number, team2Points: number): Promise<boolean> => {
    try {
      const matchData = await getMatch(matchId)
      if (!matchData) return false
      
      const pointsPerSet = matchData.pointsPerSet || 25 // Default to 25 for rally scoring
      const setsPerMatch = matchData.setsPerMatch || 3
      const currentSet = matchData.currentSet || 1
      const winByTwo = matchData.winByTwo !== false // Default to true
      const pointsStructure = matchData.pointsStructure || 'winByTwo'

      // If Fixed Points mode is enabled
      if (pointsStructure === 'fixed') {
        // Set is complete when the cumulative total equals the target
        return (team1Points + team2Points) >= pointsPerSet
      }

      // If Golden Point is enabled (winByTwo is false)
      if (!winByTwo) {
        // Set is complete when a team reaches the target points
        return team1Points >= pointsPerSet || team2Points >= pointsPerSet
      }

      // If both teams have reached pointsPerSet - 1 (set point situation)
      if (team1Points >= pointsPerSet - 1 && team2Points >= pointsPerSet - 1) {
        // Set is complete when one team leads by 2 points
        return Math.abs(team1Points - team2Points) >= 2
      }
      
      // Normal set completion (win by 2 points)
      if (team1Points >= pointsPerSet && team1Points - team2Points >= 2) return true
      if (team2Points >= pointsPerSet && team2Points - team1Points >= 2) return true
      
      return false
    } catch (error) {
      console.error('Error checking set completion:', error)
      return false
    }
  }

  // Cleanup old controller tokens
  const cleanupOldControllers = async (matchId: string, cutoffMinutes: number = 10) => {
    try {
      const controllersRef = ref(database, `matches/${matchId}/controllers`)
      const controllersSnapshot = await get(controllersRef)
      
      if (!controllersSnapshot.exists()) return
      
      const controllers = controllersSnapshot.val()
      const cutoffTime = Date.now() - (cutoffMinutes * 60 * 1000)
      const controllersToRemove: string[] = []
      
      // Find controllers older than cutoff time, EXCEPT controller #1 (host)
      Object.keys(controllers).forEach(token => {
        const controller = controllers[token]
        if (controller.createdAt < cutoffTime && controller.controllerNumber !== 1) {
          controllersToRemove.push(token)
        }
      })
      
      // Remove old controllers
      if (controllersToRemove.length > 0) {
        const updates: any = {}
        controllersToRemove.forEach(token => {
          updates[`matches/${matchId}/controllers/${token}`] = null
        })
        
        await update(ref(database), updates)
      }
    } catch (error) {
      console.error('Error cleaning up old controllers:', error)
    }
  }

  // Handle set completion with point (add the point first, then complete the set)
  const handleSetCompletionWithPoint = async (winningTeam: 'team1' | 'team2', team1Score: number, team2Score: number) => {
    try {
      // Check if already in progress to prevent duplicate calls
      if (setCompletionInProgressRef.current) {
        return;
      }
      
      // Set the flag to prevent duplicate calls
      setCompletionInProgressRef.current = true;
      
      // Get current match data FIRST to check if another controller already completed it
      const matchData = await getMatch(matchId)
      if (!matchData) {
        setCompletionInProgressRef.current = false;
        return;
      }
      
      // Get current set scores to check if this set was already added
      const currentSetScores = matchData.setScores || []
      const expectedSetCount = setScores.length
      
      // If the set count in Firebase is already higher, another controller completed it
      if (currentSetScores.length > expectedSetCount) {
        console.log('⚠️ Set already completed by another controller (set count changed), skipping');
        setCompletionInProgressRef.current = false;
        setCompletionActiveRef.current = false;
        setPendingSetCompletion(null);
        return;
      }
      
      // If pendingSetCompletion is already null in Firebase, another controller completed it
      if (!matchData.pendingSetCompletion) {
        console.log('⚠️ Set already completed by another controller (pendingSetCompletion cleared), skipping');
        setCompletionInProgressRef.current = false;
        setCompletionActiveRef.current = false;
        setPendingSetCompletion(null);
        return;
      }
      
      // First, clear pendingSetCompletion to act as a lock
      const matchRef = ref(database, `matches/${matchId}`)
      await update(matchRef, {
        pendingSetCompletion: null
      })
      
      // Add the point that completes the set
      await updateScore(matchId, team1Score, team2Score, winningTeam)
      await addScoreEvent(matchId, winningTeam, 1, 'set')
      
      // Add the completed set to the set scores
      const newSetScore = { 
        team1: team1Score, 
        team2: team2Score 
      }
      const updatedSetScores = [...currentSetScores, newSetScore]
                      
      // Update Firebase with new set score and reset point scores
      // Note: pendingSetCompletion already cleared above as a lock
      const matchRef2 = ref(database, `matches/${matchId}`)
      await update(matchRef2, {
        setScores: updatedSetScores,
        scores: { team1: 0, team2: 0, updatedAt: Date.now(), cooldown: 0, lastScoringTeam: null },
        currentSet: (matchData.currentSet || 1) + 1
      })
      
      // Update local state
      setSetScores(updatedSetScores)
      setTeam1Score(0)
      setTeam2Score(0)
      
      // Clear the pending set completion
      setPendingSetCompletion(null)
      
      // Clear the set completion active ref
      setCompletionActiveRef.current = false
      
      // Reset the flag after successful completion
      setCompletionInProgressRef.current = false;
      
      lastSuccessfulCommandTimeRef.current = Date.now()
      
    } catch (error: any) {
      console.error('Error completing set with point:', error)
      
      // Reset the flag on error
      setCompletionInProgressRef.current = false;
      
      showToast('Error', 'Failed to complete set. Please try again.', 'error')
    }
  }

  return (
    <Box minH="100vh" display="flex" flexDirection="column" bg="#09090b" overflow="hidden" color="white">
      <IPhoneModal isOpen={showIPhoneModal} onClose={() => setShowIPhoneModal(false)} />
      <InternetConnectionModal speechNetworkError={hasNetworkError} />
      {/* Remove the old buttons from here */}
      <Box position="absolute" top={4} right={4} display="flex" gap={2}>
      </Box>

        <QRCodeModal
          isOpen={showQRCodeModal}
          onClose={() => setShowQRCodeModal(false)}
          matchId={matchId}
        isController={isController}
        />

      {/* Kicked Controller Message */}
      {isKicked && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="#09090b"
          zIndex={999999}
          display="flex"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          color="white"
          onClick={() => window.location.href = '/'}
          cursor="pointer"
        >
          <Box p={8}>
            <Text fontSize="xl" fontWeight="bold" mb={4} color="#3182ce">
              Thanks for playing!
            </Text>
            <Text mb={4}>
              The match has been reset, please re-join to control the score.
            </Text>
            <Button
              colorScheme="blue"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                window.location.href = '/';
              }}
              color="#3182ce"
              borderColor="#3182ce"
              _hover={{ bg: 'rgba(49, 130, 206, 0.1)' }}
            >
              Back
            </Button>
          </Box>
        </Box>
      )}

      {/* Exit Confirmation */}
      <ExitModal
        isOpen={isExitOpen}
        onClose={() => setIsExitOpen(false)}
        onConfirm={() => {
          setIsExitOpen(false)
          window.location.href = '/'
        }}
      />

      {/* Set Completion Confirmation */}
      <ScaleFade in={pendingSetCompletion !== null} initialScale={0.9} unmountOnExit>
        <Box
          position="fixed"
          top="20%"
          left="50%"
          transform="translateX(-50%)"
          bg={pendingSetCompletion?.team === 'team1' ? 
            (team1Color === 'blue' ? '#2e4fcb' : 
             team1Color === 'red' ? '#dd0000' : 
             team1Color === 'green' ? '#38a169' : 
             team1Color === 'orange' ? '#f73e01' : 
             team1Color === 'purple' ? '#8f00c7' : 
             team1Color === 'yellow' ? '#e2e700' : `${team1Color}.100`) :
            (team2Color === 'blue' ? '#2e4fcb' : 
             team2Color === 'red' ? '#dd0000' : 
             team2Color === 'green' ? '#38a169' : 
             team2Color === 'orange' ? '#f73e01' : 
             team2Color === 'purple' ? '#8f00c7' : 
             team2Color === 'yellow' ? '#e2e700' : `${team2Color}.100`)}
          color={pendingSetCompletion?.team === 'team1' ? 
            (team1Color === 'yellow' ? 'black' : 'white') :
            (team2Color === 'yellow' ? 'black' : 'white')}
          p={6}
          borderRadius="lg"
          textAlign="center"
          zIndex={1000}
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.8)"
          minWidth="800px"
          maxWidth="1400px"
        >
          <Text fontSize="120px" fontWeight="bold" mb={2}>
            SET!
          </Text>
          <Text fontSize="2xl">
            {setCompletionTimer}
          </Text>
          <Text fontSize="lg" mt={2} mb={4}>
            {isController ? (
              <></>
            ) : (
              <>Display Only</>
            )}
          </Text>
          {isController && (
            <Button
              colorScheme="red"
              variant="outline"
              size="lg"
              onClick={() => handleCommand(['cancel'])}
              bg="#dd0000"
              color="white"
              border="2px solid"
              borderColor="#dd0000"
              mt={2}
            >
              Say "Cancel" to stop
            </Button>
          )}
        </Box>
      </ScaleFade>

      <ScaleFade in={lastError === 'Microphone off. Try Refresh or change settings.'} initialScale={0.9} unmountOnExit>
        <Box
          position="fixed"
          top="20%"
          left="50%"
          transform="translateX(-50%)"
          bg="red.500"
          color="white"
          p={6}
          borderRadius="lg"
          textAlign="center"
          zIndex={1000}
          boxShadow="xl"
        >
          <Text fontSize="xl" fontWeight="bold">
            Microphone off. Try <Button variant="link" color="white" textDecoration="underline" onClick={() => window.location.reload()}>Refresh</Button> or change settings.
          </Text>
        </Box>
      </ScaleFade>

      <VStack spacing={8} flex={1} justify="start" mt="60px" mb="32px">
        {/* Display set scores */}
        <VStack spacing={4} w="100%" justify="center" align="center" px={4} mt={2}>
          <HStack spacing={4} w="100%" justify="center" align="center" flexWrap="nowrap">
          {setScores.map((score, index) => (
              <Text 
                key={index}
                fontSize={{ 
                  base: "2xl",
                  md: "3xl"
                }} 
                color="white"
                whiteSpace="nowrap"
                fontWeight="bold"
              >
                Set {index + 1}:{' '}
                <Text as="span" color={getTeamColor(team1Color)}>{score.team1}</Text>
                {'-'}
                <Text as="span" color={getTeamColor(team2Color)}>{score.team2}</Text>
            </Text>
          ))}
          {/* Always show current set score */}
          <Text 
            fontSize={{ 
              base: "5xl",
              md: "6xl"
            }} 
            fontWeight="bold"
            color="white"
            whiteSpace="nowrap"
          >
            Set {setScores.length + 1}:{' '}
            <Text as="span" color={getTeamColor(team1Color)}>
              {team1Score}
            </Text>
            {'-'}
            <Text as="span" color={getTeamColor(team2Color)}>
              {team2Score}
            </Text>
          </Text>
          </HStack>
        </VStack>

        <HStack spacing={8} w="100%" justify="center" flexWrap="nowrap" align="stretch">
          <ScoreBox
            color={team1Color}
            score={team1Score}
            name={team1Name}
            isController={isController}
            onClick={() => handleCommand([`${team1Color} point`])}
            isDarkTheme={isDarkTheme}
            team1Name={team1Name}
            team2Name={team2Name}
            isTeam1={true}
            lastScoringTeam={lastScoringTeam}
          />

          <ScoreBox
            color={team2Color}
            score={team2Score}
            name={team2Name}
            isController={isController}
            onClick={() => handleCommand([`${team2Color} point`])}
            isDarkTheme={isDarkTheme}
            team1Name={team1Name}
            team2Name={team2Name}
            isTeam1={false}
            lastScoringTeam={lastScoringTeam}
          />
        </HStack>

        <VStack spacing={2} align="center">
          <HStack spacing={2} justify="center">
            <SayReversePointButton
              isDisabled={isInCooldown.current || !!pendingSetCompletion}
              onClick={() => handleCommand([VALID_COMMANDS.REVERSE_POINT])}
              isController={isController}
            />
            <SayResetMatchButton
              isDisabled={isInCooldown.current || !!pendingSetCompletion}
              onClick={() => handleCommand([VALID_COMMANDS.RESET_MATCH])}
              isController={isController}
            />
          </HStack>
          <ClubLogo variant="inline" responsive={true} />
        </VStack>
      </VStack>

      <Box position="fixed" bottom={0} left={0} right={0} width="100vw" margin={0} padding={0}>
        {isController && isQRCodeVisible && (
          <Box
            position="fixed"
            bottom={4}
            left={4}
            bg="white"
            p={2}
            borderRadius="md"
            boxShadow="lg"
            zIndex={1001}
          >
            <QRCode
              value={`${window.location.origin}?matchId=${matchId}&token=${controllerToken}&controller=true`}
              size={100}
              style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            />
          </Box>
        )}
        <Box position="fixed" bottom={0} left={0} right={0} width="100vw" margin={0} padding={0}>
          <Text
            position="absolute"
            left="50%"
            bottom="4px"
            transform="translateX(-50%)"
            color="white"
            fontSize="sm"
            fontWeight="normal"
            zIndex={1001}
          >
            AiScoringApp.com
          </Text>
        </Box>

        <Box 
          p={4} 
          minH="auto" 
          position="fixed"
          top={0}
          left={0}
          right={0}
          bg="#09090b"
          zIndex={999}
        >
          <TopBar
            transcript={transcript}
            error={lastError}
            onOpenMicSettings={() => setShowMicrophoneSettings(true)}
            onOpenQRModal={() => setShowQRCodeModal(true)}
            onOpenExitModal={handleExit}
            onOpenStatistics={() => setShowStatistics(true)}
            onToggleFullscreen={toggleFullscreen}
          />

          <Collapse in={showHelp} animateOpacity>
            <Box
              ref={helpRef}
              mt={4}
              p={4}
              bg="gray.100"
              borderRadius="md"
              fontSize="sm"
              position="relative"
            >
              <IconButton
                aria-label="Close help"
                icon={<FaTimes />}
                size="sm"
                position="absolute"
                top={2}
                right={2}
                onClick={() => setShowHelp(false)}
                colorScheme="gray"
              />
              {/* <Text fontWeight="bold" mb={2} color="black">Available Commands:</Text>
              <VStack align="start" spacing={1} style={{ display: 'none' }}>
                <Text color="black">• ...</Text>
                <Text color="black">• ...</Text>
                <Text color="black">• ...</Text>
                <Text color="black">• ...</Text>
              </VStack> */}
              <Button
                leftIcon={<FaChartBar />}
                onClick={() => {
                  setShowStatistics(true)
                  setShowHelp(false)
                }}
                w="100%"
                justifyContent="flex-start"
                bg="#191919"
                _hover={{ bg: '#2a2a2a' }}
                color="white"
                fontWeight="normal"
                border="1px solid"
                borderColor="gray.600"
                mb={2}
              >
                Statistics
              </Button>
              <Button
                leftIcon={<FaMicrophone />}
                onClick={() => setShowMicrophoneSettings(true)}
                w="100%"
                justifyContent="flex-start"
                bg="#191919"
                _hover={{ bg: '#2a2a2a' }}
                color="white"
                fontWeight="normal"
                border="1px solid"
                borderColor="gray.600"
                mb={2}
              >
                Microphone Settings
              </Button>

              <Button
                leftIcon={<FaExpand />}
                onClick={() => {
                  if (document.fullscreenElement) {
                    document.exitFullscreen()
                  } else {
                    document.documentElement.requestFullscreen()
                  }
                  setShowHelp(false)
                }}
                w="100%"
                justifyContent="flex-start"
                bg="#191919"
                _hover={{ bg: '#2a2a2a' }}
                color="white"
                fontWeight="normal"
                border="1px solid"
                borderColor="gray.600"
                mb={2}
              >
                Fullscreen
              </Button>

              <Button
                leftIcon={<FaSignOutAlt />}
                onClick={handleExit}
                w="100%"
                justifyContent="flex-start"
                bg="red.500"
                _hover={{ bg: 'red.600' }}
                color="white"
                fontWeight="normal"
                border="1px solid"
                borderColor="red.600"
              >
                Exit Match
              </Button>
            </Box>
          </Collapse>

        <MicrophoneSettingsModal
          isOpen={showMicrophoneSettings}
          onClose={() => setShowMicrophoneSettings(false)}
        />

        <Statistics
          isOpen={showStatistics}
          onClose={() => setShowStatistics(false)}
          matchId={matchId}
          team1Color={team1Color}
          team2Color={team2Color}
          team1Name={team1Name}
          team2Name={team2Name}
          setScores={setScores}
          currentSetScore={{
            team1: team1Score,
            team2: team2Score
          }}
        />
      </Box>

      <Box position="fixed" bottom={4} right={4} zIndex={1002}>
        <HStack spacing={3} align="center">
          <FormControl display="flex" alignItems="center">
            <FormLabel mb="0" color="#747474" fontWeight="normal"></FormLabel>
            <Switch
              isChecked={isDarkTheme}
              onChange={(e) => setIsDarkTheme(e.target.checked)}
              colorScheme="blue"
            />
          </FormControl>
          {courtNumber && !isIndividualPlan && (
            <Box
              color="white"
              px={3}
              py={1}
              fontSize="lg"
              fontWeight="bold"
              whiteSpace="nowrap"
            >
              Court {courtNumber}
            </Box>
          )}
        </HStack>
      </Box>


    </Box>

    {/* Reset Confirmation Popup */}
    {pendingReset && (
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="rgba(0, 0, 0, 0.9)"
        p={8}
        borderRadius="lg"
        zIndex={1000}
        textAlign="center"
        width="300px"
      >
        <Text color="white" fontSize="xl" mb={4}>
          To reset say "<Button 
            variant="link" 
            color="white" 
            textDecoration="underline" 
            fontSize="xl" 
            fontWeight="normal"
            onClick={handleConfirmReset}
          >Confirm</Button>"
        </Text>
        <Text color="white" fontSize="6xl" fontWeight="bold">
          {pendingReset.timer}
        </Text>
        <Text color="white" fontSize="sm" opacity={0.8}>
          Dis-connects old controllers
        </Text>
          <Text color="white" fontSize="sm" opacity={0.8}>
                      (joined &gt;10min ago)
        </Text>
      </Box>
    )}
  </Box>
)
} 